import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useToast } from '../../contexts';
import { supabase } from '../../lib/supabase';
import { UnifiedLoadingSpinner } from '../ui';

const ResetPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  
  // 定数定義
  const ERROR_MESSAGES = {
    INVALID_LINK: '無効なリセットリンクです',
    SESSION_FAILED: 'セッションの設定に失敗しました',
    PASSWORD_REQUIRED: 'パスワードを入力してください',
    PASSWORD_LENGTH: 'パスワードは8文字以上で入力してください',
    PASSWORD_COMPLEXITY: 'パスワードは大文字、小文字、数字を含む必要があります',
    CONFIRM_PASSWORD_REQUIRED: 'パスワード確認を入力してください',
    PASSWORD_MISMATCH: 'パスワードが一致しません',
    UPDATE_FAILED: 'パスワードの更新に失敗しました',
    UPDATE_SUCCESS: 'パスワードが正常に更新されました',
  };

  const REDIRECT_URL = '/auth?mode=login';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});

  // URLパラメータからトークンを取得
  const token = searchParams.get('token');
  const type = searchParams.get('type');

  // エラー状態をuseRefで管理（無限ループを防ぐため）
  const hasShownErrorRef = useRef(false);

  // エラーハンドリング関数を安定化
  const handleError = useCallback((message: string, title: string) => {
    if (!hasShownErrorRef.current) {
      hasShownErrorRef.current = true;
      showError(message, title);
      navigate(REDIRECT_URL);
    }
  }, [showError, navigate, REDIRECT_URL]);

  useEffect(() => {
    // エラーメッセージが既に表示されている場合は何もしない
    if (hasShownErrorRef.current) {
      return;
    }

    // パスワードリセットトークンが存在しない場合はログインページにリダイレクト
    if (!token || type !== 'recovery') {
      handleError(ERROR_MESSAGES.INVALID_LINK, 'エラー');
      return;
    }

    // パスワードリセットトークンを検証
    const verifyToken = async () => {
      try {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        const { error } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'recovery',
        });

        if (error) {
          console.error('Token verification error:', error);
          
          if (error.message.includes('expired') || error.message.includes('already confirmed')) {
            handleError('メールアドレス確認リンクの有効期限が切れているか、すでに確認済みです', 'エラー');
          } else {
            handleError(ERROR_MESSAGES.SESSION_FAILED, 'エラー');
          }
        }
      } catch (err) {
        console.error('Token verification failed:', err);
        handleError(ERROR_MESSAGES.SESSION_FAILED, 'エラー');
      }
    };

    verifyToken();
  }, [token, type, handleError, ERROR_MESSAGES.INVALID_LINK, ERROR_MESSAGES.SESSION_FAILED]);

  // フォームバリデーション
  const validateForm = (): boolean => {
    const errors: { password?: string; confirmPassword?: string } = {};

    if (!password) {
      errors.password = ERROR_MESSAGES.PASSWORD_REQUIRED;
    } else if (password.length < 8) {
      errors.password = ERROR_MESSAGES.PASSWORD_LENGTH;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      errors.password = ERROR_MESSAGES.PASSWORD_COMPLEXITY;
    }

    if (!confirmPassword) {
      errors.confirmPassword = ERROR_MESSAGES.CONFIRM_PASSWORD_REQUIRED;
    } else if (password !== confirmPassword) {
      errors.confirmPassword = ERROR_MESSAGES.PASSWORD_MISMATCH;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 入力値変更ハンドラー
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'password') {
      setPassword(value);
    } else if (name === 'confirmPassword') {
      setConfirmPassword(value);
    }

    // エラーをクリア
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // フォーム送信ハンドラー
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // パスワードリセットトークンを使用してパスワードを更新
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      setIsSuccess(true);
      showSuccess(ERROR_MESSAGES.UPDATE_SUCCESS, '更新完了');
      
      // 3秒後にログインページにリダイレクト
      setTimeout(() => {
        navigate(REDIRECT_URL);
      }, 3000);
    } catch (err: any) {
      console.error('Password update error:', err);
      
      // エラーメッセージを適切に処理
      let errorMessage = ERROR_MESSAGES.UPDATE_FAILED;
      if (err.message) {
        if (err.message.includes('expired') || err.message.includes('already confirmed')) {
          errorMessage = 'リセットリンクの有効期限が切れているか、すでに使用済みです';
        } else if (err.message.includes('invalid')) {
          errorMessage = '無効なリセットリンクです';
        }
      }
      
      showError(errorMessage, 'エラー');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="w-full max-w-md mx-auto text-center">
        <div className="mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            パスワード更新完了
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            パスワードが正常に更新されました。
            新しいパスワードでログインしてください。
          </p>
        </div>
        <div className="text-sm text-gray-500">
          ログインページに自動的に移動します...
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
        新しいパスワードの設定
      </h2>

      <p className="text-center text-gray-600 mb-6">
        新しいパスワードを入力してください。
      </p>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            新しいパスワード
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={password}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-blue-500 pr-10 ${
                formErrors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="新しいパスワードを入力"
              disabled={isLoading}
              required
              data-testid="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              data-testid="password-toggle-button"
            >
              {showPassword ? (
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>
          {formErrors.password && (
            <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
            パスワード確認
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-blue-500 pr-10 ${
                formErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="パスワードを再入力"
              disabled={isLoading}
              required
              data-testid="confirm-password-input"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              data-testid="confirm-password-toggle-button"
            >
              {showConfirmPassword ? (
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>
          {formErrors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          data-testid="submit-button"
          className={`w-full py-2 px-4 rounded-md text-white font-medium focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center ${
            isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {isLoading ? (
            <>
                              <UnifiedLoadingSpinner size="sm" color="white" variant="spinner" className="mr-2" />
              <span role="status">更新中</span>
            </>
          ) : (
            'パスワードを更新'
          )}
        </button>
      </form>

      <div className="mt-6 text-center text-sm text-gray-600">
        ログインページに戻る場合は{' '}
        <button
          onClick={() => navigate('/auth?mode=login')}
          className="font-medium text-blue-600 hover:text-blue-500"
        >
          こちら
        </button>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
